import {inject, Injectable} from '@angular/core';
import {UserProfileDto, UserRole} from '../../../models/generated-models';
import {toSignal} from '@angular/core/rxjs-interop';
import {filter, map, switchMap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {BehaviorSubject, Observable, catchError, shareReplay, tap, throwError} from 'rxjs';
import {UserProfileService} from '../../pages/user-profile/user-profile.service';

type ResourceAccess = Record<string, {roles?: UserRole[]}>;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private oidc = inject(OidcSecurityService);
  private readonly profileService = inject(UserProfileService);

  /** Latest personal profile (incl. delegated authorities); null until loaded. */
  private readonly profileSubject = new BehaviorSubject<UserProfileDto | null>(null);
  readonly profile$ = this.profileSubject.asObservable();
  readonly profile = toSignal(this.profile$, {initialValue: null as UserProfileDto | null});

  /** Guards the profile fetch so every subscriber shares one HTTP request. */
  private profileRequest$: Observable<UserProfileDto> | null = null;

  /**
   * Load the current user's personal profile once: concurrent subscribers share
   * the single in-flight request, and the loaded value is replayed to late
   * subscribers through the subject.
   */
  loadUserProfile(): Observable<UserProfileDto> {
    if (!this.profileRequest$) {
      this.profileRequest$ = this.profileService.getPersonalProfile().pipe(
        tap((profile) => this.profileSubject.next(profile)),
        catchError((error) => {
          this.profileRequest$ = null;
          return throwError(() => error);
        }),
        shareReplay(1),
      );
    }
    return this.profileRequest$;
  }

  /** Drop the cached profile and re-fetch. Call after the profile is updated. */
  refreshUserProfile(): Observable<UserProfileDto> {
    this.profileRequest$ = null;
    this.profileSubject.next(null);
    return this.loadUserProfile();
  }

  /**
   * Force a fresh access token + user data so client roles just mapped in
   * Keycloak apply without re-login, then re-fetch the profile.
   */
  refreshSessionAndProfile(): Observable<UserProfileDto> {
    return this.oidc.forceRefreshSession().pipe(switchMap(() => this.refreshUserProfile()));
  }

  /** True when the current profile carries the given (possibly delegated) authority. */
  hasAuthority(authority: string): boolean {
    return (this.profileSubject.value?.authorities ?? []).includes(authority);
  }

  readonly userRoles$ = this.oidc.userData$.pipe(
    filter(e => !!e?.userData),
    map(u => {
      const resourceRoles: UserRole[] = u.userData?.resource_access?.[environment.clientId]?.roles || [];
      resourceRoles.push(...u.userData?.resource_access?.['acfs-supplychain']?.roles || []);
      const realmRoles: UserRole[] = u.userData?.realm_access?.roles || [];
      return [...resourceRoles, ...realmRoles];
    })
  );

  readonly userRoles = toSignal(this.userRoles$, {initialValue: [] as UserRole[]});
  readonly resourceAccess$ = this.oidc.userData$.pipe(
    map(u => (u.userData?.resource_access ?? {}) as ResourceAccess)
  );

  readonly resourceAccess = toSignal(this.resourceAccess$, {initialValue: {} as ResourceAccess});

  /**
   * Roles used for UI gating: the union of the OIDC token roles and the
   * profile's authorities. Delegated act-as roles (FARMER_AGENT / MIDDLEMAN_AGENT)
   * are derived server-side from the role assignment and never appear in the
   * token, so both sources are required for role checks to be correct.
   */
  effectiveRoles(): string[] {
    return [...this.userRoles(), ...(this.profile()?.authorities ?? [])];
  }

  hasAny(checkRoles: string[] = [], excludeRoles?: UserRole[]): boolean {
    if (!checkRoles?.length) {
      return false;
    }
    return matchesAny(this.userRoles(), checkRoles, excludeRoles);
  }

  hasAnyOnce(checkRoles: string[], excludeRoles?: UserRole[]): Observable<boolean> {
    return this.userRoles$.pipe(
      map(roles => {
        if (!checkRoles?.length) {
          return true;
        }
        return matchesAny(roles, checkRoles, excludeRoles);
      })
    );
  }

  hasClientAccess(clientIds: string[] = []): boolean {
    return clientIds.some(clientId => (this.resourceAccess()[clientId]?.roles?.length ?? 0) > 0);
  }
}

function matchesAny(userRoles: string[], checkRoles: string[], excludeRoles?: string[]): boolean {
  const hasRequiredRole = checkRoles.some(role => userRoles.includes(role));
  if (excludeRoles?.length) {
    const hasExcludedRole = userRoles.some(role => excludeRoles.includes(role));
    return hasRequiredRole && !hasExcludedRole;
  }
  return hasRequiredRole;
}
